import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RuleInterface } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyInputModule } from '@vendasta/galaxy/input';
import { Subject } from 'rxjs';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { AutomationReplacementChipsComponent } from '../../../../../common/components';
import { AutomationVariableMenuComponent } from '../../../../../common/components/automation-variable-menu/automation-variable-menu.component';
import { insertVariable } from '../../../../../common/components/automation-variable-menu/automation-variable-menu.service';
import { FulfillmentAgentSelectorService } from '../../../../../data-services/fulfillment-agent-selector.service';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { nonNegativeIntegerValidator } from '../../../common/utils/number-validator';

// Note: This is the current level of granularity available to fulfillment agents
const TASK_TYPE = 'digital_agent';

@Component({
  selector: 'automata-create-task-panel',
  templateUrl: './create-fulfillment-task-panel.component.html',
  styleUrls: ['./create-fulfillment-task-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyInputModule,
    AutomationVariableMenuComponent,
    AutomationReplacementChipsComponent,
  ],
})
export class CreateFulfillmentTaskPanelComponent extends CommonActionPanelComponent implements OnInit, OnDestroy {
  destroyed$$ = new Subject<void>();

  form: UntypedFormGroup;
  fulfillmentAgentFormControl: UntypedFormControl = new UntypedFormControl();
  fulfillmentAgentSelectorService = this.injector.get(FulfillmentAgentSelectorService);

  constructor(
    protected readonly automationService: AutomationViewService,
    private injector: Injector,
    protected readonly translateService: TranslateService,
    private readonly ref: ChangeDetectorRef,
  ) {
    super();
    this.automationService
      .isNonPartnerAutomation()
      .pipe(
        take(1),
        map((isNonPartnerAutomation) => {
          if (isNonPartnerAutomation) {
            this.fulfillmentAgentFormControl.disable();
            return true;
          }
          return false;
        }),
      )
      .subscribe();
  }

  ngOnInit(): void {
    super.ngOnInit();
    const jsonData = this.step ? JSON.parse(this.step.data) : { assignees: [] };
    const initialSelectedPerson = jsonData.assignees[0] || null;
    this.fulfillmentAgentFormControl.setValue(initialSelectedPerson);
    this.form = new UntypedFormGroup({});
    this.form.addControl('title', new UntypedFormControl(jsonData?.title || '', Validators.required));
    this.form.addControl('assignee', this.fulfillmentAgentFormControl);
    this.form.addControl(
      'days_to_complete',
      new FormControl<number>(jsonData?.days_to_complete || '', [Validators.required, nonNegativeIntegerValidator]),
    );

    const daysToComplete = this.form.get('days_to_complete');
    daysToComplete.valueChanges
      .pipe(
        tap((c) => {
          if (c < 0) {
            daysToComplete.setValue(0, { emitEvent: false });
          }
        }),
        takeUntil(this.destroyed$$),
      )
      .subscribe();

    this.ref.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    if (!this.form) {
      return jsonData;
    }
    jsonData.title = this.form.controls.title.value;
    jsonData.assignees = [this.form.controls.assignee.value];
    jsonData.days_to_complete = this.form.controls.days_to_complete.value;
    jsonData.task_type = TASK_TYPE;
    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.form];
  }

  isFormValid(): boolean {
    return this.form.valid;
  }

  insertVariable(elementID: string, control: AbstractControl, variable: string): void {
    insertVariable(elementID, control, variable);
  }

  finishedInitialLoading(): void {
    this.savingEnabled = true;
  }
}
